import { Button } from 'antd'
import React, { useState } from 'react'
import TextArea from 'antd/es/input/TextArea'
import { useParams } from 'react-router-dom'
import { BODY_PARTS, REASON_CHARS_MINIMUM } from '../../../constants'
import { BodyPartSelector, VERTICAL } from './BodyPartSelector'
import { FormLabel } from '../../../components/form/FormLabel'
import { useReactivateSubject } from '../../../hooks/useSubject'

type Props = {
  subject: any
  onClose?: () => void
}

export const ReactivateSubjectDrawer = ({ subject, onClose = () => {} }: Props) => {
  const { projectId, siteId } = useParams() as { projectId: string; siteId: string }

  const [reActivateReason, setReActivateReason] = useState<string>('')

  const currentPhase = subject && subject.currentPhase ? subject.currentPhase : {}

  const [selectedBodyParts, setSelectedBodyParts] = useState<string>()

  const [reasonError, setReasonError] = useState<string | null>(null)
  const [bodyPartsError, setBodyPartsError] = useState<string | null>(null)

  const { callReactivate, isLoading } = useReactivateSubject(projectId, subject._id, siteId)

  const plainOptions = currentPhase?.bodyParts
    ? currentPhase.bodyParts.map((item) => {
        return { bodyPart: item, label: BODY_PARTS[item] || item }
      })
    : []

  console.log('ReactivateSubjectDrawer > plainOptions = ', plainOptions)

  const hideContent = () => {
    setReActivateReason('')
    onClose()
  }

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setReasonError(null)
    setReActivateReason(e.target.value)
  }

  const handleSelectBodyPartChange = (bodyPart: string) => {
    setBodyPartsError(null)
    setSelectedBodyParts(bodyPart)
  }

  const handleReactivation = () => {
    // If there are body parts options available but no option has been chosen -> set error and leave
    if (plainOptions.length > 0 && !selectedBodyParts) {
      setBodyPartsError('Please select a body part')
      return
    }

    // If the number of characters in the reason text field is less than the required minimum  -> set error and leave
    if (reActivateReason.length < REASON_CHARS_MINIMUM) {
      setReasonError(`Please provide a reason minimum ${REASON_CHARS_MINIMUM} characters long`)
      return
    }

    const obj = {
      projectId: projectId,
      subjectId: subject._id,
      reasonDescription: reActivateReason,
      bodyPart: selectedBodyParts,
    }

    return callReactivate(obj)
      .then(() => {
        setReActivateReason('')

        onClose()
      })
      .catch((error) => {
        alert(error.message)
      })
  }

  return (
    <div className="container-fluid h-100 d-flex flex-column">
      <div className="d-flex align-items-center mb-2">
        <h4 className="mb-0">Participant reactivation</h4>
      </div>

      {currentPhase?.bodyParts?.length !== 0 ? (
        <>
          <p className="mt-2">
            Reactivate this participant and resume ePRO data collection. The subject will return to their previous
            phase. The participant’s device will be re-enabled to continue study participation.
          </p>

          <BodyPartSelector
            className="mt-3 mb-0"
            bodyparts={plainOptions}
            direction={VERTICAL}
            setSelectedBodyPart={handleSelectBodyPartChange}
          ></BodyPartSelector>
          <p className="p-small text-danger" style={{ marginTop: '-8px' }}>
            {bodyPartsError || '\u00A0'}
          </p>
        </>
      ) : (
        <>
          <p className="mt-2">
            Reactivate this participant and resume ePRO data collection. Provide a written reason below. The participant
            will return to their previous phase.
          </p>
          <p className="mt-3 mb-2">The participant’s device will be re-enabled to continue study participation.</p>
        </>
      )}

      <div className="mt-3">
        <FormLabel label="Provide justification:"></FormLabel>
        <TextArea placeholder="" rows={2} className="mt-1" onChange={handleTextChange} value={reActivateReason} />
        <p className="p-small text-danger">{reasonError || '\u00A0'}</p>
      </div>

      <div style={{ marginTop: 24, textAlign: 'right' }}>
        <Button onClick={hideContent}>Cancel</Button>

        <Button className="ms-2" type="primary" loading={isLoading} onClick={handleReactivation}>
          Reactivate Participant
        </Button>
      </div>
    </div>
  )
}
